import classNames from 'classnames';
import { useState } from 'react';
import { Optional } from '../../../../Common/interfaces';
import {
    FormValidation,
    FormValidationMessage,
    TextField,
    ValidationStatus,
} from '../../../../components';
import {
    GhostButton,
    LabelBody,
    LabelHeader4,
    SecondaryButton,
    Spacer,
    SwirlyBox,
} from '../../components';
import { CloseIcon } from '../../icons';
import { useSyncedStateForJSONArrays } from '../../useSyncedState';
import { defaultActions } from './defaultActions';

const builtInItems = defaultActions.map((x) => x.name);

type Page2Props = {
    selectedItems: Optional<string>;
    onContinue: (selectedItems: string[]) => void;
};
export function Page2({ selectedItems: s, onContinue }: Page2Props) {
    const [selectedItems, setSelectedItems] = useSyncedStateForJSONArrays(s, 0);
    const [items, setItems] = useState<string[]>(() => {
        return Array.from(new Set(builtInItems.concat(selectedItems)));
    });
    const [newItem, setNewItem] = useState('');
    const [newItemValidationStatus, setNewItemValidationStatus] =
        useState<Optional<ValidationStatus>>();
    return (
        <>
            <LabelHeader4 className="mb-10">Dine livsområder</LabelHeader4>
            <LabelBody className="mb-10">
                Først skal du kigge nærmere på de forskellige dele af dit liv -
                altså de områder, du bruger din tid og energi på i hverdagen.
            </LabelBody>
            <LabelBody className="mb-10">
                Dine livsområder kan være konkrete, som f.eks. familie eller
                job, eller abstrakte, som f.eks. sundhed eller kærlighed.
            </LabelBody>
            <LabelBody>
                Du kan vælge op til seks områder, som repræsenterer dit liv lige
                nu. Vi har lavet nogle forslag for at hjælpe dig i gang.
            </LabelBody>
            <div className="my-30 space-y-20">
                {items.map((x) => (
                    <SelectableItem
                        key={x}
                        className="mr-10"
                        isSelected={selectedItems.includes(x)}
                        disabled={selectedItems.length >= 6}
                        onChangeIsSelected={(newValue) => {
                            if (newValue) {
                                setSelectedItems(selectedItems.concat(x));
                            } else {
                                setNewItemValidationStatus(null);
                                setSelectedItems(
                                    selectedItems.filter((y) => y !== x),
                                );
                            }
                        }}
                    >
                        {x}
                    </SelectableItem>
                ))}

                {selectedItems.length > 6 && (
                    <LabelBody color="text-secondary-error">
                        Der skal højst vælges 6 områder.
                    </LabelBody>
                )}
            </div>
            <LabelBody>Tilføj livsområde</LabelBody>
            <form
                className="mt-10"
                onSubmit={(e) => {
                    e.preventDefault();

                    if (!newItem) return;

                    if (selectedItems.length >= 6) {
                        setNewItemValidationStatus('error');
                        return;
                    }

                    setItems(items.concat(newItem));
                    setSelectedItems(selectedItems.concat(newItem));
                    setNewItem('');
                }}
            >
                <div className="flex items-center space-x-20">
                    <FormValidation
                        className="grow"
                        validationStatus={newItemValidationStatus}
                    >
                        <TextField
                            value={newItem}
                            onChangeValue={setNewItem}
                            placeholder="Dit livsområde..."
                        />
                    </FormValidation>
                    <GhostButton type="submit">Gem</GhostButton>
                </div>
                {newItemValidationStatus && (
                    <FormValidationMessage
                        validationStatus={newItemValidationStatus}
                    >
                        Du kan max. vælge 6 områder.
                    </FormValidationMessage>
                )}
            </form>
            <SwirlyBox className="my-30">
                Når du har tilføjet dine livsområder, kan du fortsætte
            </SwirlyBox>
            <Spacer />
            <SecondaryButton
                disabled={selectedItems.length > 6}
                onClick={() => onContinue(selectedItems)}
            >
                Fortsæt
            </SecondaryButton>
        </>
    );
}

type P = {
    children: React.ReactNode;
    isSelected: boolean;
    disabled?: boolean;
    onChangeIsSelected: (isSelected: boolean) => void;
    className?: string;
};
function SelectableItem({
    children,
    isSelected,
    disabled: _disabled,
    onChangeIsSelected,
    className,
}: P) {
    const disabled = _disabled && !isSelected;
    return (
        <div
            className={classNames(
                className,
                'inline-flex items-center whitespace-nowrap rounded-[30px] border px-10',
                {
                    'border-dark-red bg-dark-red text-white': isSelected,
                    'border-grey-text-field bg-white text-grey': !isSelected,
                    'opacity-60': disabled,
                },
            )}
            onClick={() => onChangeIsSelected(!isSelected)}
            inert={disabled ? '' : undefined}
        >
            <LabelBody>{children}</LabelBody>
            {isSelected && (
                <CloseIcon
                    className="ml-10 rounded-full bg-white text-dark-red"
                    width="16"
                    height="16"
                />
            )}
        </div>
    );
}
